import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { isDismissableImporterError } from "@common/helpers/financialDocument";
import { useRemoveImporterErrorMutation } from "@api/financialDocument.api";
import { CheckIcon, TriangleSmallIcon, AlertCircleIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Loader } from "@design-system/Loader/Loader";
import {
    getDisplayErrorMessage,
    ImporterDisplayErrorKind,
} from "@components/importer/helpers";
import { ImporterDocumentStatusMessage } from "@components/importer/ImporterDocumentStatusMessage";
import importerStyles from "@components/importer/ImporterPage.module.scss";
import { AccrualImporterContext } from "./accrualImporter.context";
import { getAccrualDocumentImportStatus } from "./accrualImporter.helpers";

interface Props {
    financialDocument: FinancialDocumentExtended;
    onClose: () => void;
    invoiceGroupsWarningSummary?: string;
}

export const AccrualPreviewCommandBar: React.FC<Props> = ({
    financialDocument,
    onClose,
    invoiceGroupsWarningSummary,
}) => {
    const { readyToImportLabel, revalidateDocument } = useContext(
        AccrualImporterContext,
    );

    const documentRow = useMemo(
        () => ({
            id: financialDocument.id.toString(),
            financialDocument,
        }),
        [financialDocument],
    );

    const importStatus = useMemo(
        () => getAccrualDocumentImportStatus(documentRow),
        [documentRow],
    );

    const displayedErrors = useMemo(
        () => (importStatus.kind === "review_tasks" ? importStatus.errors : []),
        [importStatus],
    );

    const [currentErrorIndex, setCurrentErrorIndex] = useState(0);

    useEffect(() => {
        setCurrentErrorIndex((previousIndex) =>
            Math.min(previousIndex, Math.max(displayedErrors.length - 1, 0)),
        );
    }, [displayedErrors.length]);

    const safeIndex = Math.min(
        currentErrorIndex,
        Math.max(displayedErrors.length - 1, 0),
    );
    const currentError = displayedErrors[safeIndex];

    const removeImporterErrorMutation =
        useRemoveImporterErrorMutation(financialDocument);

    const goToPreviousError = useCallback(() => {
        setCurrentErrorIndex((previousIndex) => Math.max(0, previousIndex - 1));
    }, []);

    const goToNextError = useCallback(() => {
        setCurrentErrorIndex((previousIndex) =>
            Math.min(displayedErrors.length - 1, previousIndex + 1),
        );
    }, [displayedErrors.length]);

    const dismiss = useCallback(() => {
        if (
            currentError?.kind === ImporterDisplayErrorKind.IMPORTER_EXTRACTION
        ) {
            removeImporterErrorMutation.mutate(currentError.error, {
                onSuccess: () => {
                    revalidateDocument({
                        financialDocumentId: financialDocument.id,
                    });
                },
            });
        }
    }, [
        currentError,
        financialDocument.id,
        removeImporterErrorMutation,
        revalidateDocument,
    ]);

    if (importStatus.kind === "processing") {
        return (
            <CommandIsland className={importerStyles.previewCommandBar}>
                <div className={importerStyles.commandBarStatusMessage}>
                    <Loader inline />
                    <span>Extracting...</span>
                </div>
                <div className={importerStyles.commandBarActions}>
                    <Button variant="primary" size="md" onClick={onClose}>
                        Back
                    </Button>
                </div>
            </CommandIsland>
        );
    }

    if (importStatus.kind === "review_tasks" && currentError) {
        const errorMessage = getDisplayErrorMessage(
            currentError,
            financialDocument,
        );
        const isImporterError =
            currentError.kind === ImporterDisplayErrorKind.IMPORTER_EXTRACTION;
        const importerError = isImporterError ? currentError.error : undefined;
        const isDismissable =
            importerError !== undefined &&
            isDismissableImporterError({
                error: importerError,
                importerQueue: financialDocument.importerQueue,
            });

        return (
            <CommandIsland className={importerStyles.previewCommandBar}>
                <div className={importerStyles.commandBarNav}>
                    <Button
                        variant="primary"
                        size="md"
                        iconOnly
                        aria-label="Previous error"
                        className={importerStyles.commandBarNavLeft}
                        onClick={goToPreviousError}
                        disabled={safeIndex === 0}
                    >
                        <TriangleSmallIcon className="icon-rotate-90" />
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        iconOnly
                        aria-label="Next error"
                        className={importerStyles.commandBarNavRight}
                        onClick={goToNextError}
                        disabled={safeIndex === displayedErrors.length - 1}
                    >
                        <TriangleSmallIcon className="icon-rotate-270" />
                    </Button>
                </div>
                <div className={importerStyles.commandBarMessage}>
                    <span>
                        Issue {safeIndex + 1} of {displayedErrors.length}
                    </span>
                    <span className={importerStyles.commandBarDot}>
                        &middot;
                    </span>
                    <span>{errorMessage}</span>
                </div>
                <div className={importerStyles.commandBarActions}>
                    <Button variant="primary" size="md" onClick={onClose}>
                        Back
                    </Button>
                    {isDismissable && (
                        <Button variant="primary" size="md" onClick={dismiss}>
                            Dismiss
                        </Button>
                    )}
                    {!isDismissable && (
                        <Button variant="primary" size="md" disabled>
                            Continue
                        </Button>
                    )}
                </div>
            </CommandIsland>
        );
    }

    const readyWarnings =
        importStatus.kind === "ready_with_warnings"
            ? importStatus.errors.map((error) =>
                  getDisplayErrorMessage(error, financialDocument),
              )
            : [];
    const readyWarningMessage = [invoiceGroupsWarningSummary, ...readyWarnings]
        .filter((message) => Boolean(message))
        .join(". ");

    return (
        <CommandIsland className={importerStyles.previewCommandBar}>
            {readyWarningMessage ? (
                <ImporterDocumentStatusMessage
                    icon={<AlertCircleIcon className="icon-color-yellow-600" />}
                    message={readyWarningMessage}
                    className="text-yellow-600"
                />
            ) : (
                <div className={importerStyles.commandBarReadyMessage}>
                    <CheckIcon className="icon-color-gray-900" />
                    <span>{readyToImportLabel}</span>
                </div>
            )}
            <div className={importerStyles.commandBarActions}>
                <Button variant="primary" size="md" onClick={onClose}>
                    Finish review
                </Button>
            </div>
        </CommandIsland>
    );
};
